import React, { useState, useContext } from "react"
import { Checkbox, Grid } from '@mui/material'
import { useNavigate } from "react-router-dom";
import { ExistingCard } from "../components/ExistingCard";
import { CardTypes } from "ts-interfaces";
import { useMattoState } from "../../MattoState";
import { CollectionModal } from "../../studioxr/components/CollectionPopover/CollectionModal";
// import { useLoginState } from "../../studioxr/auth/LoginState";
import { associateCollectionWithProject, deleteCollection, duplicateCollectionScheme } from "../../v2/apis/collectionsSchemeApi";
import { DeleteComponentModal } from "../components/DeleteComponentModal";
import { CollectionInterface, ProjectSchemeInterface } from "ts-interfaces";
import { ShareComponentModal } from "../components/ShareComponentModal";
import { AuthContext } from "../../auth/Auth";
import { AssociatedProjectModal } from "../components/AssociatedProjectModal";
import { usePermissions, firebaseHelper } from "ui";
import { useProjectSchemas } from "../../global-state/useProjectSchemas";
import { useCollections } from "../../global-state/useCollections";


interface DisplayCollectionSchemesProps {
    data: CollectionInterface[]; // or whatever your collection interface is called
    onCollectionUpdate: (collection: CollectionInterface, addCollection?: boolean, isDeleted?: boolean) => void;
    designFirms: any; // replace with actual type
    teamMembers: any; // replace with actual type
    associatedProjectsList: ProjectSchemeInterface[];
    cardSize?: string; // or use union type like "default" | "small" | "large" if you have specific sizes
    showCheckboxes?: boolean;
    selectedCollectionIds?: Set<string>;
    selectedProjectId?: string;
    allProjects?: ProjectSchemeInterface[];
    onCollectionSelectionChange?: (id: string, checked: boolean) => void;
    hasViewerAccess?: boolean;
}

export const DisplayCollectionSchemes = ({ 
    data, 
    onCollectionUpdate, 
    designFirms, 
    teamMembers, 
    associatedProjectsList, 
    cardSize="default",
    showCheckboxes = false,
    selectedCollectionIds = new Set(),
    selectedProjectId="",
    allProjects = [],
    onCollectionSelectionChange = (id: string, checked: boolean) => {},
    hasViewerAccess,
}: DisplayCollectionSchemesProps) => {
    const {currentUser} = useContext(AuthContext);
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showShareModal, setShowShareModal] = useState(false)
    const [selectedCollection, setSelectedCollection] = useState<CollectionInterface>({} as CollectionInterface)
    const [showAssociatedProjectModal, setShowAssociatedProjectModal] = useState(false)
    const { permissions, hasCollectionAccess, hasProjectAccess } = usePermissions(currentUser?.uid, firebaseHelper.db);
    const { getProjectOwner } = useProjectSchemas(state => ({ getProjectOwner: state.getProjectOwner }));

    const history = useNavigate();
    const setSnackbarMessage = useMattoState((state) => state.setSnackbarMessage);
   
	const handleClick = (collection) => { 
        if (!showCheckboxes) {
            history(`/project/new?collectionId=${(collection?.id || '')}&collectionType=my`); 
        }
    }

    const handleEdit = (collection) => {
        useMattoState.getState().setModal({name:'collection', id:collection.id, associatedProjectsList: associatedProjectsList, showShareCollection: false, onSave: (collection: CollectionInterface) => onCollectionUpdate?.(collection, false) });    
    }

    const handleCopy = async (e,collection) => {
        e.stopPropagation()
        e.preventDefault()
        setSnackbarMessage('Duplicating collection...');
        const duplicatedCollection = await duplicateCollectionScheme(collection, permissions);
        
        // Only add to current list if the duplicated collection belongs here
        // In CollectionsPanel (cardSize='small'), we're showing collections for a specific project,
        // so we shouldn't add the duplicated collection since it now has no associatedProjectId
        // In CollectionScheme (cardSize='default'), we're showing user's own collections,
        // so we should add it since it's now owned by the current user
        const shouldAddToCurrentList = cardSize !== 'small';
        
        if (shouldAddToCurrentList) {
            onCollectionUpdate?.(duplicatedCollection, true);
        } else {
            useCollections.getState().saveCollection(duplicatedCollection);
        }
        
        setSnackbarMessage('Collection duplicated successfully');
    }

    const openDeleteModal = (e,collection) => {
        e.stopPropagation()
        setSelectedCollection(collection)
        setShowDeleteModal(true)
    }

    const handleDelete = async () => {
        try {
            setSnackbarMessage('Deleting collection...');
            await deleteCollection(selectedCollection, permissions);
            setShowDeleteModal(false);
            onCollectionUpdate?.(selectedCollection, false, true);
            setSelectedCollection({} as CollectionInterface);
            setSnackbarMessage('Collection deleted successfully');
        } catch (error) {
            console.error('Error deleting collection:', error);
            
            // Show specific error message based on the error type
            let errorMessage = 'Failed to delete collection';
            if (error.message) {
                if (error.message.includes('permission')) {
                    errorMessage = 'You don\'t have permission to delete this collection';
                } else if (error.message.includes('not found')) {
                    errorMessage = 'Collection not found or already deleted';
                } else {
                    errorMessage = error.message;
                }
            }
            
            setSnackbarMessage(errorMessage);
            setShowDeleteModal(false);
            setSelectedCollection({} as CollectionInterface);
        }
    }

    const openShareModal = (e,collection) => {
        e.stopPropagation()
        setSelectedCollection(collection)
        setShowShareModal(true)
    }

    const handleShareCollection = () => {
        if (selectedCollection) {
            navigator.clipboard.writeText(`${window.location.origin}/project/new?collectionId=${selectedCollection.id}`)
            setSnackbarMessage(`Collection URL copied to clipboard`)
        }
    }

    const openAssociatedProjectModal = (e,collection) => {
        e.stopPropagation()
        setSelectedCollection(collection)
        setShowAssociatedProjectModal(true)
    }

    const handleAssociatedProjectChange = async ({ newProjectId, oldProjectId, collection=null }) => {
        const targetCollection = collection || selectedCollection; // fallback to selectedCollection if collection not provided
        if(!targetCollection) return;
        console.log('permissions', permissions);
        try {
            await associateCollectionWithProject(targetCollection.id, newProjectId, currentUser.uid, permissions);
            
            // Get the full collection from the store to preserve all data
            const collectionsState = useCollections.getState();
            const fullCollection = collectionsState.collections.find(c => c.id === targetCollection.id);
            
            if (!fullCollection) {
                console.error('Collection not found in store:', targetCollection.id);
                return;
            }
            
            const updatedCollection = {
                ...fullCollection, // Use the full collection to preserve all properties
                associatedProjectId: newProjectId,
                updatedAt: Date.now()
            };
            
            // If moving from one project to another
            if (oldProjectId && oldProjectId !== newProjectId) {
                useProjectSchemas.getState().removeCollectionFromProjects(targetCollection.id);
            }
            
            // Add to new project
            if (newProjectId) {
                useProjectSchemas.getState().updateCollectionInProjects(updatedCollection);
            }
            
            // Update collection store
            useCollections.getState().updateCollection(updatedCollection);
            setShowAssociatedProjectModal(false);
            setSnackbarMessage('Collection association updated successfully');
        } catch (error) {
            console.error('Error updating collection association:', error);
            setSnackbarMessage(error.message || 'Failed to update collection association');
        }
    }

    const handleRemoveFromProject = (collection) => {
        setSnackbarMessage('Removing collection from project...');
        setSelectedCollection(collection);
        handleAssociatedProjectChange({
            newProjectId: null,
            oldProjectId: collection?.associatedProjectId || null,
            collection: collection
        });
    };

    const handleCheckboxChange = (collectionId: string, event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        onCollectionSelectionChange(collectionId, event.target.checked);
    };

    return (
        <>
            {data.map((collection: CollectionInterface) => {
                const projectOwner = collection.associatedProjectId ? getProjectOwner(collection.associatedProjectId) : undefined;
                const hasProjectReadAccess = collection.associatedProjectId ? hasProjectAccess(collection.associatedProjectId, 'read', projectOwner) : false;
                const hasProjectWriteAccess = collection.associatedProjectId ? hasProjectAccess(collection.associatedProjectId, 'write', projectOwner) : false;
                const hasCollectionReadAccess = hasCollectionAccess(collection.id, 'read', collection.owner || collection.uid);
                const isProjectAssociated = allProjects.some(project => project.id === collection.associatedProjectId);
                const isDisabled = !!(showCheckboxes && selectedProjectId && collection.associatedProjectId && isProjectAssociated && (collection.associatedProjectId !== selectedProjectId));
                const hasCollectionWriteAccess = hasCollectionAccess(collection.id, 'write', collection.owner || collection.uid);
                // Use prop-level hasViewerAccess if provided, otherwise calculate it individually
                const computedHasViewerAccess = hasViewerAccess !== undefined ? hasViewerAccess : 
                    (hasCollectionReadAccess || hasProjectReadAccess) && !hasCollectionWriteAccess && !hasProjectWriteAccess;

    
                
                return (
                                        <Grid 
                        key={collection.id} 
                        item 
                        sx={{ position: 'relative' }}>
                        {showCheckboxes && (
                            <Checkbox
                                checked={selectedCollectionIds.has(collection.id)}
                                onChange={(e) => handleCheckboxChange(collection.id, e)}
                                disabled={isDisabled}
                                aria-label={collection.name}
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    zIndex: 1,
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    borderRadius: '4px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                    }
                                }}
                            />
                        )}
                        <ExistingCard 
                            data={{
                                ...collection,
                                name: collection?.name,
                                thumbnail: collection?.thumbnails,
                                updatedAt: collection?.updatedAt
                            }} 
                            coverImage={collection?.coverImage || null}
                            designFirms={designFirms} 
                            teamMembers={teamMembers} 
                            type={CardTypes.Collections}
                            onClick={() => !isDisabled && handleClick(collection)} 
                            onEdit={() => !isDisabled && handleEdit(collection)}
                            onDelete={(e) => !isDisabled && openDeleteModal(e,collection)} 
                            onShare={(e) => !isDisabled && openShareModal(e,collection)}
                            onCopy={(e) => !isDisabled && handleCopy(e,collection)} 
                            onAssociatedProjectChange={(e) => !isDisabled && openAssociatedProjectModal(e, collection)}
                            onRemoveFromProject={() => !isDisabled && handleRemoveFromProject(collection)}
                            cardSize={cardSize}
                            hasViewerAccess={computedHasViewerAccess}
                            isDisabled={isDisabled}
                        />
                    </Grid>
                )}
            )}
            <DeleteComponentModal open={showDeleteModal} setOpen={setShowDeleteModal} onDelete={handleDelete} />
            <ShareComponentModal name={selectedCollection.name} open={showShareModal} setOpen={setShowShareModal} onShare={handleShareCollection} />
            <AssociatedProjectModal open={showAssociatedProjectModal} onClose={() => setShowAssociatedProjectModal(false)}  onSave={handleAssociatedProjectChange} data={selectedCollection} type={CardTypes.Collections} /> 
            <CollectionModal />
        </>
    )
}